import * as THREE from "three";
import type { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { createThickLineSegments, setThickLinePoints } from "../../../entities/_shared";
import type { HelperLine } from "../../../geometry";

const GUIDE_COLOR = 0xb4b2a9;
// Helper lines are conceptually infinite; this is long enough to leave the viewport at any sane zoom.
const GUIDE_HALF_LENGTH_M = 1000;
const GUIDE_RENDER_ORDER = 997;

// CSS pixels — thinner than the outline, so guides read as construction aids rather than geometry.
const GUIDE_WIDTH_PX = 1.5;

// Dash and gap are in scene metres. The helper lines span 2 km, so their pattern has to be coarse
// enough not to become a solid smear at map zoom.
const HELPER_LINE_DASH_SIZE = 6;
const HELPER_LINE_GAP_SIZE = 12;
const GHOST_EDGE_DASH_SIZE = 1.5;
const GHOST_EDGE_GAP_SIZE = 1;

/**
 * The dashed construction lines shown while an edge is being offset: the infinite lines of the two
 * neighbouring edges that its ends slide along, plus a ghost of where the edge started.
 *
 * Owned by EdgeEditHandler rather than by the shape — it exists only for the duration of a drag.
 */
class EdgeOffsetGuides {
	public readonly sceneObject = new THREE.Group();

	private readonly previousHelperLine: LineSegments2;
	private readonly nextHelperLine: LineSegments2;
	private readonly ghostEdge: LineSegments2;

	constructor() {
		this.previousHelperLine = createGuideLine({ dashSize: HELPER_LINE_DASH_SIZE, gapSize: HELPER_LINE_GAP_SIZE });
		this.nextHelperLine = createGuideLine({ dashSize: HELPER_LINE_DASH_SIZE, gapSize: HELPER_LINE_GAP_SIZE });
		this.ghostEdge = createGuideLine({ dashSize: GHOST_EDGE_DASH_SIZE, gapSize: GHOST_EDGE_GAP_SIZE });

		this.sceneObject.add(this.previousHelperLine, this.nextHelperLine, this.ghostEdge);
		this.sceneObject.visible = false;
	}

	public show({
		helperLines,
		edgeStart,
		edgeEnd,
	}: {
		helperLines: { previous: HelperLine | null; next: HelperLine | null };
		edgeStart: THREE.Vector3;
		edgeEnd: THREE.Vector3;
	}): void {
		setInfiniteLine({ line: this.previousHelperLine, helperLine: helperLines.previous });
		setInfiniteLine({ line: this.nextHelperLine, helperLine: helperLines.next });
		setThickLinePoints({ line: this.ghostEdge, points: [edgeStart, edgeEnd] });

		this.sceneObject.visible = true;
	}

	public hide(): void {
		this.sceneObject.visible = false;
	}
}

function createGuideLine({ dashSize, gapSize }: { dashSize: number; gapSize: number }): LineSegments2 {
	// createThickLineSegments enables no picking layers, so guides never snap or get selected.
	const line = createThickLineSegments({
		materialDefinition: {
			color: GUIDE_COLOR,
			linewidth: GUIDE_WIDTH_PX,
			dashed: true,
			dashSize,
			gapSize,
			// Late render pass, so renderOrder can lift the guides above the fills.
			transparent: true,
			depthTest: false,
			depthWrite: false,
		},
	});

	line.renderOrder = GUIDE_RENDER_ORDER;
	return line;
}

function setInfiniteLine({ line, helperLine }: { line: LineSegments2; helperLine: HelperLine | null }): void {
	if (helperLine === null) {
		line.visible = false;
		return;
	}

	const direction = helperLine.direction.clone().setY(0);

	if (direction.lengthSq() === 0) {
		line.visible = false;
		return;
	}

	direction.normalize().multiplyScalar(GUIDE_HALF_LENGTH_M);

	setThickLinePoints({
		line,
		points: [helperLine.point.clone().sub(direction), helperLine.point.clone().add(direction)],
	});
}

export { EdgeOffsetGuides };
